import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Button } from '@signozhq/ui/button';
import { Checkbox } from '@signozhq/ui/checkbox';
import { Input } from '@signozhq/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@signozhq/ui/select';
import logEvent from 'api/common/logEvent';
import { ClarificationFieldTypeDTO } from 'api/ai-assistant/sigNozAIAssistantAPI.schemas';
import type {
	ClarificationEventDTO,
	ClarificationFieldEventDTO,
} from 'api/ai-assistant/sigNozAIAssistantAPI.schemas';
import { CircleHelp, Send, X } from '@signozhq/icons';

import { AIAssistantEvents } from '../../events';
import { useAIAssistantAnalyticsContext } from '../../hooks/useAIAssistantAnalyticsContext';
import { useAIAssistantStore } from '../../store/useAIAssistantStore';

import styles from './ClarificationForm.module.scss';

/** Sentinel emitted by the select dropdown when the user picks the custom slot. */
const CUSTOM_OPTION_SENTINEL = '__signoz_ai_custom__';
/** User-facing label for the synthetic "type your own answer" option. */
const CUSTOM_OPTION_LABEL = 'Other (type your own)';

interface ClarificationFormProps {
	conversationId: string;
	clarification: ClarificationEventDTO;
}

/**
 * Rendered when the agent emits a `clarification` SSE event.
 * Dynamically renders form fields based on the `fields` array and
 * submits answers to resume the agent on a new execution.
 */
export default function ClarificationForm({
	conversationId,
	clarification,
}: ClarificationFormProps): JSX.Element {
	const { t } = useTranslation('aiAssistant');
	const submitClarification = useAIAssistantStore((s) => s.submitClarification);
	const cancelStream = useAIAssistantStore((s) => s.cancelStream);
	const isStreaming = useAIAssistantStore(
		(s) => s.streams[conversationId]?.isStreaming ?? false,
	);
	const { threadId, page, mode } =
		useAIAssistantAnalyticsContext(conversationId);

	const fields = clarification.fields ?? [];
	const initialAnswers = Object.fromEntries(
		fields.map((f) => [f.id, initialAnswerFor(f)]),
	);
	const [answers, setAnswers] =
		useState<Record<string, unknown>>(initialAnswers);
	const [submitted, setSubmitted] = useState(false);
	const [cancelled, setCancelled] = useState(false);

	const setField = (id: string, value: unknown): void => {
		setAnswers((prev) => ({ ...prev, [id]: value }));
	};

	const isFormValid = fields.every(
		(f) => !f.required || isFieldFilled(f, answers[f.id]),
	);

	const handleSubmit = async (): Promise<void> => {
		if (!isFormValid) {
			return;
		}
		setSubmitted(true);
		// Approximate queryLength as the JSON encoding of the form answers — the
		// clarification API doesn't render a single user-visible string, but the
		// JSON size is a reasonable stand-in for "how much did the user provide".
		const queryLength = JSON.stringify(answers).length;
		void logEvent(AIAssistantEvents.MessageSent, {
			threadId,
			page,
			mode,
			queryLength,
			hasContext: false,
			respondingToClarification: true,
		});
		await submitClarification(
			conversationId,
			clarification.clarificationId,
			answers,
		);
	};

	const handleCancel = (): void => {
		setCancelled(true);
		void logEvent(AIAssistantEvents.CancelClicked, {
			threadId,
			secondsSinceStart: null,
		});
		cancelStream(conversationId);
	};

	if (submitted) {
		return (
			<div className={cx(styles.clarification, styles.submitted)}>
				<Send size={13} className={styles.icon} />
				<span className={styles.statusText}>Answers submitted — resuming…</span>
			</div>
		);
	}

	if (cancelled) {
		return (
			<div className={cx(styles.clarification, styles.submitted)}>
				<X size={13} className={styles.icon} />
				<span className={styles.statusText}>Request cancelled.</span>
			</div>
		);
	}

	return (
		<div className={styles.clarification}>
			<div className={styles.header}>
				<CircleHelp size={13} className={styles.headerIcon} />
				<span className={styles.headerLabel}>{t('details_needed')}</span>
			</div>

			<p className={styles.message}>{clarification.message}</p>

			<div className={styles.fields}>
				{fields.map((field) => (
					<FieldInput
						key={field.id}
						field={field}
						value={answers[field.id]}
						onChange={(val): void => setField(field.id, val)}
					/>
				))}
			</div>

			<div className={styles.actions}>
				<Button
					variant="solid"
					color="primary"
					onClick={handleSubmit}
					disabled={isStreaming || !isFormValid}
					prefix={<Send />}
				>
					{t('submit')}
				</Button>
				<Button
					variant="outlined"
					color="secondary"
					onClick={handleCancel}
					disabled={isStreaming}
					prefix={<X />}
				>
					{t('cancel_request')}
				</Button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Field renderer — covers every variant of ClarificationFieldTypeDTO:
// text, number, select, multi_select, boolean.
// ---------------------------------------------------------------------------

/**
 * Per-type seed value. The DTO's `default` is `string | string[] | null`,
 * which doesn't fit boolean / number fields cleanly — we coerce 'true'/'false'
 * strings for booleans, parse number defaults out of the string form,
 * fall back to `[]` for multi_select, and the raw string otherwise.
 */
function initialAnswerFor(f: ClarificationFieldEventDTO): unknown {
	const raw = f.default;
	if (f.type === ClarificationFieldTypeDTO.boolean) {
		// `default` is typed string | string[] | null; backend sends
		// 'true'/'false' as strings for boolean fields.
		return raw === 'true';
	}
	if (f.type === ClarificationFieldTypeDTO.multi_select) {
		return Array.isArray(raw) ? raw : [];
	}
	if (f.type === ClarificationFieldTypeDTO.number) {
		// Server sends number defaults as strings (e.g. `"5"`). Parse so the
		// stored value is a real `number` — otherwise `isFieldFilled` (which
		// requires `typeof === 'number'`) rejects a visibly-filled field and
		// Submit stays disabled.
		if (typeof raw !== 'string' || raw === '') {
			return null;
		}
		const parsed = Number(raw);
		return Number.isNaN(parsed) ? null : parsed;
	}
	return raw ?? '';
}

// Whether a required field has been answered. Booleans are always considered
// filled (they're initialised to a concrete true/false). For other types we
// reject empty strings, empty arrays, NaN numbers, and `null` (which the
// number input emits when its raw value is `''` — `Number('')` would
// otherwise silently coerce to `0` and read as a valid answer).
function isFieldFilled(
	field: ClarificationFieldEventDTO,
	value: unknown,
): boolean {
	switch (field.type) {
		case ClarificationFieldTypeDTO.multi_select:
			return Array.isArray(value) && value.length > 0;
		case ClarificationFieldTypeDTO.boolean:
			return true;
		case ClarificationFieldTypeDTO.number:
			return typeof value === 'number' && !Number.isNaN(value);
		default:
			return typeof value === 'string' && value.trim().length > 0;
	}
}

interface FieldInputProps {
	field: ClarificationFieldEventDTO;
	value: unknown;
	onChange: (value: unknown) => void;
}

function FieldInput({ field, value, onChange }: FieldInputProps): JSX.Element {
	const { t } = useTranslation('aiAssistant');
	const { id, type, label, required, options, allowCustom } = field;

	// Local UI state for the synthetic "custom" option on select /
	// multi_select fields with `allowCustom`. The free-text input only renders
	// when this is true; the typed value is what's actually sent up via
	// `onChange` (never the sentinel / "Other" label).
	const [isCustom, setIsCustom] = useState(false);
	const [customValue, setCustomValue] = useState('');

	// Render the select if the field has options OR if the server marked it
	// `allowCustom` (in which case the dropdown still appears with just the
	// "Other (type your own)" entry — a plain `options: null` would
	// otherwise fall through to the bare text-input renderer).
	if (type === ClarificationFieldTypeDTO.select && (options || allowCustom)) {
		const handleSelectChange = (next: string | string[]): void => {
			// `multiple` is off → callback receives a single string. The wider
			// `string | string[]` typing comes from the shared Select root.
			const picked = Array.isArray(next) ? (next[0] ?? '') : next;
			if (picked === CUSTOM_OPTION_SENTINEL) {
				setIsCustom(true);
				onChange(customValue);
			} else {
				setIsCustom(false);
				onChange(picked);
			}
		};

		return (
			<div className={styles.field}>
				<label className={styles.label} htmlFor={id}>
					{label}
					{required && (
						<span className={styles.required} aria-hidden="true">
							*
						</span>
					)}
				</label>
				<Select
					value={isCustom ? CUSTOM_OPTION_SENTINEL : String(value ?? '')}
					onChange={handleSelectChange}
				>
					<SelectTrigger
						id={id}
						placeholder={t('select_placeholder')}
						aria-required={required || undefined}
					/>
					{/* Pin the dropdown width to the trigger via Radix's
					    `--radix-select-trigger-width`; otherwise the popover
					    sizes to its widest item and looks misaligned. */}
					<SelectContent className={styles.selectContent}>
						{options?.map((opt) => (
							<SelectItem key={opt} value={opt}>
								{opt}
							</SelectItem>
						))}
						{allowCustom && (
							<SelectItem value={CUSTOM_OPTION_SENTINEL}>
								{CUSTOM_OPTION_LABEL}
							</SelectItem>
						)}
					</SelectContent>
				</Select>
				{isCustom && (
					<Input
						type="text"
						className={styles.input}
						placeholder={t('enter_custom_value')}
						value={customValue}
						onChange={(e): void => {
							setCustomValue(e.target.value);
							onChange(e.target.value);
						}}
					/>
				)}
			</div>
		);
	}

	// Boolean — single yes/no checkbox. The label sits inside the checkbox
	// so the click target covers both, matching how multi_select rows render.
	if (type === ClarificationFieldTypeDTO.boolean) {
		const checked = value === true;
		return (
			<div className={styles.field}>
				<Checkbox
					className={styles.checkboxLabel}
					value={checked}
					onChange={(): void => onChange(!checked)}
				>
					{label}
					{required && (
						<span className={styles.required} aria-hidden="true">
							*
						</span>
					)}
				</Checkbox>
			</div>
		);
	}

	// Same fallback logic as the select branch — render the checkbox group
	// when there are options OR when the field is `allowCustom` only.
	if (
		type === ClarificationFieldTypeDTO.multi_select &&
		(options || allowCustom)
	) {
		const selected = Array.isArray(value) ? (value as string[]) : [];
		// Anything in the value array that isn't one of the predefined options
		// is treated as a custom entry — we keep at most one custom entry,
		// driven by the local `customValue` + `isCustom` state below.
		const regularSelected = selected.filter((v) => options?.includes(v));

		const toggleRegular = (opt: string): void => {
			const nextRegular = regularSelected.includes(opt)
				? regularSelected.filter((v) => v !== opt)
				: [...regularSelected, opt];
			onChange(
				isCustom && customValue ? [...nextRegular, customValue] : nextRegular,
			);
		};

		const toggleCustom = (): void => {
			if (isCustom) {
				setIsCustom(false);
				onChange(regularSelected);
			} else {
				setIsCustom(true);
				onChange(customValue ? [...regularSelected, customValue] : regularSelected);
			}
		};

		const updateCustomValue = (next: string): void => {
			setCustomValue(next);
			if (isCustom) {
				onChange(next ? [...regularSelected, next] : regularSelected);
			}
		};

		return (
			// `fieldset` + `legend` is the WCAG-recommended grouping for
			// related checkboxes (1.3.1). SRs announce the legend before each
			// option, so users hear the group label as context.
			<fieldset
				className={styles.multiSelectFieldset}
				aria-required={required || undefined}
			>
				<legend className={styles.label}>
					{label}
					{required && (
						<span className={styles.required} aria-hidden="true">
							*
						</span>
					)}
				</legend>
				<div className={styles.checkboxGroup}>
					{options?.map((opt) => (
						<Checkbox
							key={opt}
							className={styles.checkboxLabel}
							value={regularSelected.includes(opt)}
							onChange={(): void => toggleRegular(opt)}
						>
							{opt}
						</Checkbox>
					))}
					{allowCustom && (
						<Checkbox
							className={styles.checkboxLabel}
							value={isCustom}
							onChange={toggleCustom}
						>
							{CUSTOM_OPTION_LABEL}
						</Checkbox>
					)}
				</div>
				{isCustom && (
					<Input
						type="text"
						className={styles.input}
						placeholder={t('enter_custom_value')}
						value={customValue}
						onChange={(e): void => updateCustomValue(e.target.value)}
					/>
				)}
			</fieldset>
		);
	}

	// text / number (default)
	return (
		<div className={styles.field}>
			<label className={styles.label} htmlFor={id}>
				{label}
				{required && (
					<span className={styles.required} aria-hidden="true">
						*
					</span>
				)}
			</label>
			<Input
				id={id}
				type={type === 'number' ? 'number' : 'text'}
				className={styles.input}
				value={String(value ?? '')}
				aria-required={required || undefined}
				onChange={(e): void => {
					if (type === 'number') {
						const raw = e.target.value;
						// Map empty input to `null` instead of `Number('') === 0`
						// so a required numeric field cleared after typing doesn't
						// silently read as a valid `0` in `isFieldFilled`.
						onChange(raw === '' ? null : Number(raw));
					} else {
						onChange(e.target.value);
					}
				}}
				placeholder={label}
			/>
		</div>
	);
}
